/**
 * Contracts — company-wide Signport contract tracking (08/21).
 *
 * Every Signport contract across every merchant contact, in one place.
 * Signport stays the source of truth: this page reads the CRM's Signport
 * index (which stores only the document ID plus send-time context) and that
 * index is kept current by Signport webhooks, with a pull-on-read fallback.
 *
 * Contracts are STARTED from a merchant contact profile ("Create New Deal"),
 * never from a funded deal page — funded deals hold funded transactions only.
 * A completed contract becomes a funded deal here, explicitly, via Convert.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Search, RefreshCw, MoreHorizontal, ExternalLink, User, Link2, Send,
  MessageSquare, BellRing, XCircle, Download, ShieldCheck, Briefcase,
  FileSignature, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  { key: 'sent', label: 'Sent', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  { key: 'viewed', label: 'Viewed', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  { key: 'partially_signed', label: 'Partially Signed', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  { key: 'completed', label: 'Completed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { key: 'expired', label: 'Expired', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
  { key: 'cancelled', label: 'Cancelled', cls: 'bg-red-100 text-red-700 border-red-300' },
];

const statusMeta = (key) => STATUSES.find(s => s.key === key)
  || { key, label: key, cls: 'bg-gray-100 text-gray-700 border-gray-300' };

const money = (v) => (v === null || v === undefined || v === ''
  ? '—'
  : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));

const day = (v) => (v ? String(v).slice(0, 10) : '—');

const sinceNow = (v) => {
  if (!v) return '—';
  const then = new Date(v);
  if (Number.isNaN(then.getTime())) return day(v);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days < 30 ? `${days}d ago` : day(v);
};

const expiryTone = (contract) => {
  if (!contract.expires_at) return 'text-gray-500';
  if (['completed', 'cancelled', 'expired'].includes(contract.status)) return 'text-gray-500';
  const left = (new Date(contract.expires_at).getTime() - Date.now()) / 86400000;
  if (Number.isNaN(left)) return 'text-gray-500';
  if (left < 0) return 'text-red-600 font-medium';
  if (left <= 3) return 'text-amber-600 font-medium';
  return 'text-gray-600';
};

const Contracts = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [contracts, setContracts] = useState([]);
  const [counts, setCounts] = useState({});
  const [configured, setConfigured] = useState(true);
  const [webhooksConfigured, setWebhooksConfigured] = useState(true);
  const [signportBase, setSignportBase] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertDealRef, setConvertDealRef] = useState('');
  const [converting, setConverting] = useState(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get(`${API}/contracts`, {
        params: { refresh: true },
        headers: getAuthHeaders(),
      });
      setContracts(res.data.contracts || []);
      setCounts(res.data.counts || {});
      setConfigured(res.data.configured !== false);
      setWebhooksConfigured(res.data.webhooks_configured !== false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // The Signport dashboard link needs the tenant's Signport host.
    axios.get(`${API}/signport/config`, { headers: getAuthHeaders() })
      .then(res => setSignportBase(res.data?.base_url || null))
      .catch(() => setSignportBase(null));
  }, [getAuthHeaders]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return contracts.filter((c) => {
      if (status !== 'all' && c.status !== status) return false;
      if (!needle) return true;
      const hay = [
        c.merchant?.name, c.merchant?.dba, c.template?.name, c.sender,
        c.document_id, ...(c.recipients || []).map(r => r.email),
      ];
      return hay.some(v => v && String(v).toLowerCase().includes(needle));
    });
  }, [contracts, status, search]);

  const act = async (contract, path, body, successMessage) => {
    setBusyId(contract.document_id);
    try {
      const res = await axios.post(
        `${API}/contracts/${contract.document_id}/${path}`, body || {},
        { headers: getAuthHeaders() });
      toast.success(successMessage);
      await load({ quiet: true });
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${path} this contract`);
      return null;
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (contract) => {
    let url = contract.signing_url;
    if (!url) {
      const res = await act(contract, 'refresh', {}, 'Refreshed from Signport');
      url = res?.signing_url;
    }
    if (!url) {
      toast.error('Signport has no signing link for this contract yet');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Signing link copied');
    } catch {
      window.prompt('Copy the signing link:', url);
    }
  };

  const download = async (contract, kind) => {
    const path = kind === 'audit' ? 'audit-certificate' : 'pdf';
    setBusyId(contract.document_id);
    try {
      const res = await axios.get(`${API}/contracts/${contract.document_id}/${path}`,
        { headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const merchant = (contract.merchant?.name || 'contract').replace(/[^\w-]+/g, '_');
      link.setAttribute('download',
        kind === 'audit' ? `${merchant}_audit_certificate.pdf` : `${merchant}_signed_contract.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // A blob-typed error body has to be read back as text to surface it.
      let detail = 'Download failed';
      try { detail = JSON.parse(await err.response.data.text()).detail || detail; } catch { /* keep default */ }
      toast.error(detail);
    } finally {
      setBusyId(null);
    }
  };

  const openInSignport = (contract) => {
    if (!signportBase) {
      toast.error('Signport base URL is not configured on this backend');
      return;
    }
    window.open(`${signportBase}/documents/${contract.document_id}`, '_blank', 'noopener');
  };

  // --- Conversion -----------------------------------------------------------
  // Signing a contract never creates a deal. The operator either creates the
  // funded deal now (pre-filled from the contract, confirmed in the normal
  // deal form) or links a funded deal that already exists.
  const startNewDeal = (contract) => {
    const t = contract.terms || {};
    sessionStorage.setItem('preFillDeal', JSON.stringify({
      company_id: contract.merchant?.contact_id,
      company_name: contract.merchant?.name,
      dba: contract.merchant?.dba,
      email: contract.merchant?.email,
      phone: contract.merchant?.phone,
      funding_amount: t.funding_amount ?? '',
      payback: t.payback ?? '',
      rate: t.factor ?? '',
      term_days: t.term_days ?? '',
      payment_amount: t.payment_amount ?? '',
      fees: t.fees ?? '',
      schedule: t.schedule ?? '',
    }));
    // Picked up after the deal is created, to attach the signed PDF, the audit
    // certificate and the Signport document ID to the new deal.
    sessionStorage.setItem('pendingContractLink', JSON.stringify({
      documentId: contract.document_id,
      contactId: contract.merchant?.contact_id,
    }));
    setConvertTarget(null);
    navigate('/deals?createNew=true');
  };

  const linkExistingDeal = async () => {
    if (!convertDealRef.trim()) {
      toast.error('Enter the funded deal number');
      return;
    }
    setConverting(true);
    const res = await act(convertTarget, 'convert', { deal_ref: convertDealRef.trim() },
      'Contract converted — signed PDF filed on the deal');
    setConverting(false);
    if (res) {
      setConvertTarget(null);
      setConvertDealRef('');
      navigate(`/deals/${res.deal_id}`);
    }
  };

  const outstanding = (c) => !['completed', 'cancelled', 'expired'].includes(c.status);

  return (
    <Layout>
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileSignature className="h-6 w-6 text-blue-600" />
              Contracts
            </h1>
            <p className="text-sm text-gray-500">
              Every Signport contract across all merchant contacts. Start a new one from a
              merchant contact profile.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                className="pl-8 w-72"
                placeholder="Merchant, template, sender, recipient…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="contracts-search"
              />
            </div>
            <Button variant="outline" onClick={() => load()} disabled={loading}
              data-testid="contracts-refresh">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {!configured && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Signport isn&apos;t configured — set <code>SIGNPORT_BASE_URL</code> and{' '}
              <code>SIGNPORT_API_KEY</code> on the backend service, then redeploy.
            </span>
          </div>
        )}
        {configured && !webhooksConfigured && (
          <div className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded p-3 flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Live status push is off — set <code>SIGNPORT_WEBHOOK_SECRET</code> and point
              Signport at <code>/api/contracts/webhook</code>. Until then statuses refresh
              when this page loads.
            </span>
          </div>
        )}

        {/* Status filters */}
        <div className="flex flex-wrap gap-2" data-testid="contracts-filters">
          {STATUSES.map(s => {
            const count = s.key === 'all' ? (counts.all ?? contracts.length) : (counts[s.key] ?? 0);
            const active = status === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setStatus(s.key)}
                data-testid={`contracts-filter-${s.key}`}
                className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                  active
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {s.label}
                <span className={`ml-1.5 text-xs ${active ? 'text-blue-100' : 'text-gray-400'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="contracts-table">
                <thead className="bg-gray-50 text-gray-600 border-b">
                  <tr>
                    {['Merchant', 'Template', 'Sender', 'Recipients', 'Funding', 'Payback',
                      'Status', 'Expires', 'Created', 'Last activity', ''].map((h, i) => (
                      <th key={h || i}
                        className={`px-3 py-2 font-medium whitespace-nowrap ${
                          ['Funding', 'Payback'].includes(h) ? 'text-right' : 'text-left'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading && (
                    <tr><td colSpan={11} className="px-3 py-10 text-center text-gray-500">
                      Loading contracts…
                    </td></tr>
                  )}
                  {!loading && visible.length === 0 && (
                    <tr><td colSpan={11} className="px-3 py-10 text-center text-gray-500">
                      No contracts match this view. Start one from a merchant contact profile.
                    </td></tr>
                  )}
                  {!loading && visible.map((c) => {
                    const meta = statusMeta(c.status);
                    return (
                      <tr key={c.document_id || c.id} className="hover:bg-gray-50"
                        data-testid={`contract-row-${c.document_id}`}>
                        <td className="px-3 py-2">
                          {c.merchant?.contact_id ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/contacts/${c.merchant.contact_id}`)}
                              className="text-blue-600 hover:underline font-medium text-left"
                              data-testid={`contract-merchant-${c.document_id}`}
                            >
                              {c.merchant?.name || 'Unnamed merchant'}
                            </button>
                          ) : (
                            <span className="text-gray-500">{c.merchant?.name || '—'}</span>
                          )}
                          {c.merchant?.dba && (
                            <div className="text-xs text-gray-400">dba {c.merchant.dba}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{c.template?.name || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{c.sender || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {(c.recipients || []).length === 0 && '—'}
                          {(c.recipients || []).map(r => (
                            <div key={r.email} className="whitespace-nowrap">
                              {r.email}
                              {r.signed_at && <span className="text-emerald-600 text-xs ml-1">· signed</span>}
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{money(c.funding_amount)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{money(c.payback_amount)}</td>
                        <td className="px-3 py-2">
                          <Badge className={meta.cls}>{meta.label}</Badge>
                          {c.deal && (
                            <div className="text-[11px] text-gray-500 mt-0.5">
                              → deal #{c.deal.number}
                            </div>
                          )}
                        </td>
                        <td className={`px-3 py-2 whitespace-nowrap ${expiryTone(c)}`}>{day(c.expires_at)}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{day(c.created_at)}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{sinceNow(c.last_activity_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" disabled={busyId === c.document_id}
                                data-testid={`contract-actions-${c.document_id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              <DropdownMenuItem onClick={() => openInSignport(c)}>
                                <ExternalLink className="h-4 w-4 mr-2" /> Open contract
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={!c.merchant?.contact_id}
                                onClick={() => navigate(`/contacts/${c.merchant.contact_id}`)}>
                                <User className="h-4 w-4 mr-2" /> Open merchant contact
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => copyLink(c)}>
                                <Link2 className="h-4 w-4 mr-2" /> Copy signing link
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={!outstanding(c)}
                                onClick={() => act(c, 'resend', {}, 'Signing request re-sent')}>
                                <Send className="h-4 w-4 mr-2" /> Resend signing link
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={!outstanding(c)}
                                onClick={() => act(c, 'sms', {}, 'Signing link sent by SMS')}>
                                <MessageSquare className="h-4 w-4 mr-2" /> Send SMS fallback
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={!outstanding(c)}
                                onClick={() => act(c, 'remind', {}, 'Reminder sent')}>
                                <BellRing className="h-4 w-4 mr-2" /> Send reminder
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={!outstanding(c)}
                                className="text-red-600"
                                onClick={() => act(c, 'cancel', {}, 'Signing request cancelled')}>
                                <XCircle className="h-4 w-4 mr-2" /> Cancel request
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem disabled={c.status !== 'completed'}
                                onClick={() => download(c, 'pdf')}>
                                <Download className="h-4 w-4 mr-2" /> Download signed PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled={c.status !== 'completed'}
                                onClick={() => download(c, 'audit')}>
                                <ShieldCheck className="h-4 w-4 mr-2" /> Download audit certificate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={c.status !== 'completed' || Boolean(c.deal)}
                                onClick={() => { setConvertDealRef(''); setConvertTarget(c); }}
                                data-testid={`contract-convert-${c.document_id}`}>
                                <Briefcase className="h-4 w-4 mr-2" /> Convert to funded deal
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Convert — explicit, two ways, never automatic */}
      <Dialog open={Boolean(convertTarget)} onOpenChange={(o) => !o && setConvertTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Convert to funded deal</DialogTitle>
            <DialogDescription>
              {convertTarget?.merchant?.name} · {convertTarget?.template?.name}.
              Funding is confirmed separately — the signed contract does not fund itself.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border bg-gray-50 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-600">Funding</span>
                <span className="font-medium">{money(convertTarget?.funding_amount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Payback</span>
                <span className="font-medium">{money(convertTarget?.payback_amount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Factor</span>
                <span className="font-medium">{convertTarget?.terms?.factor || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Term / schedule</span>
                <span className="font-medium">
                  {convertTarget?.terms?.term_days || '—'} · {convertTarget?.terms?.schedule || '—'}
                </span></div>
            </div>

            <div className="rounded-md border p-3 space-y-2">
              <div className="font-medium text-sm">Create the funded deal now</div>
              <p className="text-xs text-gray-500">
                Opens the new-deal form pre-filled with this merchant contact and these
                terms. The signed PDF, audit certificate and Signport document ID attach
                once you save the deal.
              </p>
              <Button className="w-full" onClick={() => startNewDeal(convertTarget)}
                data-testid="convert-create-deal">
                <Briefcase className="h-4 w-4 mr-2" /> Create funded deal
              </Button>
            </div>

            <div className="rounded-md border p-3 space-y-2">
              <div className="font-medium text-sm">Link a funded deal that already exists</div>
              <Label className="text-xs">Deal number</Label>
              <Input value={convertDealRef} onChange={e => setConvertDealRef(e.target.value)}
                placeholder="10763" data-testid="convert-deal-ref" />
              <Button variant="outline" className="w-full" onClick={linkExistingDeal}
                disabled={converting} data-testid="convert-link-deal">
                {converting ? 'Linking…' : 'Link contract to this deal'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Contracts;
